import {
  ButtonProps,
  closeCurrentDialog,
  KeyboardShortcut,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {
  BaseDirectSaleMaterialItem,
  GetDirectSaleMaterialItemResponse,
  GetWarehousesResponse,
  useBulkDeleteDirectSaleItemsMutation,
  useDeleteDirectSaleMaterialBasketItemsMutation,
  useGetDirectSaleMaterialBasketItemsQuery,
  useGetDirectSaleQuery,
  useLazyGetDirectSaleMaterialItemQuery,
  usePatchDirectSaleMaterialBasketItemQuantityMutation,
  usePatchDirectSaleMaterialItemMutation,
  usePostDirectSaleCoreExchangeItemDispatchMutation,
  usePostDirectSaleMaterialBasketCheckoutMutation,
  usePostDirectSaleMaterialDispatchMutation,
  usePostDirectSaleMaterialNonBindingMutation,
  usePostDirectSaleMaterialRequestMutation,
} from '@dms/api/warehouse';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  getInvoiceName,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useBasketItemsQuantityState,
} from '@dms/shared';
import {useAfterSalesHeaderParameters, useBasketMechanic} from '@dms/shared-aftersales';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {focusGridPart} from 'features/eag-grid';

import {DS_SPARE_PARTS_GRID_CODE} from './DS_SPARE_PARTS_GRID_CODE';
import {RequestTabMaterialItemEdit} from './RequestTabMaterialItemEdit';
import {RequestTabMaterialList} from './RequestTabMaterialList';

interface RequestTabMaterialModalProps extends RequiredTestIdProps {
  directSaleId: string;
  authorizationProfileId: string | Nullish;
  onDiscard: () => void;
  availableWarehouses: GetWarehousesResponse;
}

export function RequestTabMaterialModal(props: RequestTabMaterialModalProps) {
  const [getDirectSaleMaterialItem] = useLazyGetDirectSaleMaterialItemQuery();

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'direct-sale',
    contextId: props.directSaleId,
  });

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetDirectSaleMaterialBasketItemsQuery({directSaleId: props.directSaleId});

  const {data: directSale} = useGetDirectSaleQuery({directSaleId: props.directSaleId});

  const headerParameters = useAfterSalesHeaderParameters({
    numberLabel: i18n.t('entity.warehouse.labels.directSale'),
    number: directSale?.number,
    customerLabel: i18n.t('entity.warehouse.labels.customer'),
    customerId: directSale?.customerId,
    vehicleId: directSale?.vehicleId,
  });

  const [checkoutMaterialBasket, {isLoading: isCheckingOutBasket}] =
    usePostDirectSaleMaterialBasketCheckoutMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDeleteDirectSaleMaterialBasketItemsMutation();

  const [dispatchMaterial, dispatchResult] = usePostDirectSaleMaterialDispatchMutation();

  const [requestMaterial, requestResult] = usePostDirectSaleMaterialRequestMutation();

  const [nonBindingMaterial, nonBindingResult] = usePostDirectSaleMaterialNonBindingMutation();

  const [dispatchCoreExchangeItem, coreExchangeDispatchResult] =
    usePostDirectSaleCoreExchangeItemDispatchMutation();

  const [changeMaterialBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchDirectSaleMaterialBasketItemQuantityMutation();

  const [deleteMaterialItems, {isLoading: isDeletingBasketItems}] =
    useBulkDeleteDirectSaleItemsMutation();

  const [, {isLoading: isEditingMaterialItem}] = usePatchDirectSaleMaterialItemMutation({
    fixedCacheKey: 'edit-direct-sale-material-item',
  });

  const basketItems = basket?.sparePartsBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.sparePartsBasketTotalPrice;

  const basketMechanicId = basket?.assignMechanicId;

  const isAddingItemsToBasket =
    dispatchResult.isLoading ||
    requestResult.isLoading ||
    nonBindingResult.isLoading ||
    coreExchangeDispatchResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isDiscardingBasket ||
    isCheckingOutBasket ||
    isChangingQuantity ||
    isEditingMaterialItem ||
    isDeletingBasketItems;

  const [focusTrigger, setFocusTrigger] = useState(0);

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const {mechanics, areMechanicsLoading, mechanicPayload, inputMechanicId, handleMechanicChange} =
    useBasketMechanic({
      authorizationProfileId: props.authorizationProfileId,
      assignedMechanicId: basketMechanicId,
    });

  const handleCheckout = async () => {
    if (isNotNil(mechanicPayload)) {
      return await checkoutMaterialBasket({
        directSaleId: props.directSaleId,
        body: {
          assignMechanics: mechanicPayload,
        },
      })
        .unwrap()
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded'))
        )
        .then(props.onDiscard)
        .catch(handleApiError);
    }

    await checkoutMaterialBasket({
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(refreshDocumentsCount)
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleDiscard = async () => {
    await discardBasket({directSaleId: props.directSaleId})
      .unwrap()
      .then(props.onDiscard)
      .catch(handleApiError);
  };

  const handleWarehouseDispatchMaterial = async (warehouseArticleId: string) => {
    await dispatchMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .then(() => setFocusTrigger((prev) => prev + 1))
      .catch(handleApiError);
  };

  // request/non-binding/core exchange resolve with a success flag so callers can skip cleanup on failure
  const handleWarehouseRequestMaterial = async (warehouseArticleId: string) =>
    await requestMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .then(() => {
        setFocusTrigger((prev) => prev + 1);
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

  const handleWarehouseNonBindingMaterial = async (warehouseArticleId: string) =>
    await nonBindingMaterial({directSaleId: props.directSaleId, body: {warehouseArticleId}})
      .unwrap()
      .then(() => {
        setFocusTrigger((prev) => prev + 1);
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

  const handleCoreExchangeDispatch = async (coreExchangeItemId: string) =>
    await dispatchCoreExchangeItem({directSaleId: props.directSaleId, body: {coreExchangeItemId}})
      .unwrap()
      .then(() => {
        setFocusTrigger((prev) => prev + 1);
        return true;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await changeMaterialBasketItemQuantity({
      directSaleId: props.directSaleId,
      itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = async (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError("Missing itemId, can't open editing dialog");
    }

    const materialItem = (await getDirectSaleMaterialItem({
      itemId: editingDetails.itemId,
      directSaleId: props.directSaleId,
    })
      .unwrap()
      .catch(handleApiError)) as GetDirectSaleMaterialItemResponse;

    const itemName = materialItem
      ? `${getInvoiceName(materialItem)} (${materialItem?.number})`
      : '';

    openDialog(
      <RequestTabMaterialItemEdit
        directSaleId={props.directSaleId}
        item={materialItem}
        isEditingDisabled={false}
        authorizationProfileId={props.authorizationProfileId}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('dialog.editMaterial', props)}
      />,
      {
        title: itemName,
        size: 'large',
        scrollBehavior: 'inside',
        withAdditionalFooter: true,
      }
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {items: ids.map((id) => ({id}))};

    await deleteMaterialItems({
      directSaleId: props.directSaleId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      keyboardShortcut: KeyboardShortcut.ModEnter,
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.addMaterial')}
      parameters={headerParameters}
      actions={actions}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <RequestTabMaterialList
              hasPendingRequest={hasPendingRequest}
              onWarehouseDispatch={handleWarehouseDispatchMaterial}
              onWarehouseRequest={handleWarehouseRequestMaterial}
              onWarehouseNonBinding={handleWarehouseNonBindingMaterial}
              onCoreExchangeDispatch={handleCoreExchangeDispatch}
              availableWarehouses={props.availableWarehouses}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<BaseDirectSaleMaterialItem>
              focusTrigger={focusTrigger}
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasPendingRequest,
                hasError: hasBasketError,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                selectedMechanicId: inputMechanicId,
                onMechanicChange: handleMechanicChange,
              }}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTabOut={() => focusGridPart(DS_SPARE_PARTS_GRID_CODE, 'smartSearch')}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
